import { RepeatIcon } from "@chakra-ui/icons";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { HttpClient } from "../../../application/protocols/http-client";
import { emailValidation } from "../../../application/validation/email-validation";
import { useLang } from "../../i18n/use-lang";

type ResendEmailModalProps = {
  isOpen: boolean;
  onClose(): void;
  httpClient: HttpClient<{ email: string }, any>;
};

type ResendEmailFormData = {
  email: string;
};

export default function ResendEmailModal({
  isOpen,
  onClose,
  httpClient,
}: ResendEmailModalProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResendEmailFormData>();
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const { lang, s } = useLang();

  const onSubmit = handleSubmit(async (data) => {
    try {
      setLoading(true);
      await httpClient.post({
        url: "/user/resend/link",
        body: {
          email: data.email,
        },
      });
      setLoading(false);
      onClose();
      navigate(`/verify/link/success?lang=${lang}`);
      toast({
        title: s.toastDoneTitle,
        description: s.modalSentDesc,
        status: "success",
        duration: 9000,
        isClosable: true,
      });
    } catch (error: any) {
      setLoading(false);
      if (error.response) {
        const errorObject = error.response?.data;
        toast({
          title: s.toastErrorTitle,
          description: errorObject.message,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      } else {
        toast({
          title: s.toastErrorTitle,
          description: s.genericError,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
    }
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent as="form" onSubmit={onSubmit}>
        <ModalHeader>{s.modalTitle}</ModalHeader>

        <ModalCloseButton />
        <ModalBody pb={6}>
          <Text>{s.modalInstruction}</Text>
          <FormControl marginTop={5}>
            <FormLabel>{s.emailLabel}</FormLabel>
            <Input
              placeholder={s.emailPlaceholder}
              {...register("email", {
                validate: { email: emailValidation },
              })}
              focusBorderColor="black"
              errorBorderColor="red.500"
              isInvalid={errors.email?.type === "email"}
            />
            {errors.email?.type === "email" && (
              <Text fontSize={"sm"} marginTop={1} textColor={"red.500"}>
                {s.invalidEmail}
              </Text>
            )}
          </FormControl>
        </ModalBody>

        <ModalFooter>
          <Button
            type="submit"
            disabled={loading || errors.email?.type === "email"}
            isLoading={loading}
            colorScheme="red"
            mr={3}
            leftIcon={<RepeatIcon />}
          >
            {s.modalGenerate}
          </Button>
          <Button onClick={onClose}>{s.cancel}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
